import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Check, Loader2, Link2Off } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { env } from "@/lib/env";
import { supabase } from "@/lib/supabase";

interface TelegramAuthData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

declare global {
  interface Window {
    // We share the global with TelegramLoginButton; both components are
    // single-screen anyway and re-assign on mount.
    onTelegramLinkAuth?: (user: TelegramAuthData) => void;
  }
}

interface TelegramStatus {
  is_linked: boolean;
  telegram_username: string | null;
}

async function fetchMyTelegramStatus(): Promise<TelegramStatus | null> {
  const { data, error } = await supabase.rpc("get_my_telegram_status");
  if (error) throw new Error(error.message);
  const row = (data as Array<TelegramStatus> | null)?.[0];
  return row ?? null;
}

/**
 * "Connect Telegram" block for /me/account. Different from the per-event
 * version (TelegramConnectBlock) in two ways:
 *
 *   1. The link is global to the auth.users row, not to a specific
 *      event_participants row. We also backfill chat_id across all
 *      participant rows that match this user's email, so notifications
 *      Just Work for every past/future event without per-event clicks.
 *
 *   2. We use the same Telegram Login Widget as the sign-in page (with
 *      `data-request-access="write"` so the bot gets permission to DM
 *      this user), but the auth callback goes to `telegram-link-user`
 *      instead of `telegram-login` — no new auth.users row, just a link.
 *
 * The widget script can only be present once per page; we mount it lazy
 * inside this card. If the user already linked, we skip the widget and
 * render a quiet "Connected ✓" pill.
 */
export function MyAccountTelegramBlock() {
  const queryClient = useQueryClient();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  async function handleUnlink() {
    setError(null);
    setUnlinking(true);
    try {
      const session = await supabase.auth.getSession();
      const jwt = session.data.session?.access_token;
      if (!jwt) throw new Error("Not signed in");

      const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/telegram-unlink-user`;
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      const data = (await resp.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (!resp.ok || !data.ok) {
        throw new Error(data.error || `Unlink failed (${resp.status})`);
      }
      // Hard reload — same reason as in the link flow: Telegram widget
      // debris in the DOM and stale query cache combine badly otherwise.
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't disconnect Telegram");
    } finally {
      setUnlinking(false);
    }
  }

  const { data: status, isLoading } = useQuery({
    queryKey: ["my-telegram-status"],
    queryFn: fetchMyTelegramStatus,
    staleTime: 60_000,
  });

  const isLinked = status?.is_linked ?? false;

  useEffect(() => {
    // Don't bother loading the widget if the user is already connected
    // or if we're still resolving status (avoids a brief widget flash).
    if (isLoading || isLinked) return;

    window.onTelegramLinkAuth = async (user) => {
      setError(null);
      setConflict(false);
      setPending(true);
      try {
        const session = await supabase.auth.getSession();
        const jwt = session.data.session?.access_token;
        if (!jwt) throw new Error("Not signed in");

        const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/telegram-link-user`;
        const resp = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(user),
        });
        const data = (await resp.json().catch(() => ({}))) as {
          ok?: boolean;
          error?: string;
        };

        if (resp.status === 409) {
          setConflict(true);
          throw new Error("This Telegram account is already linked to another Finally user.");
        }
        if (!resp.ok || !data.ok) {
          throw new Error(data.error || `Link failed (${resp.status})`);
        }
        // The Telegram widget leaves DOM debris behind (iframe + script
        // tags it owns) and a soft invalidate doesn't always flip the
        // "Connected ✓" state cleanly — a hard reload here guarantees the
        // UI matches the new server state.
        window.location.reload();
        return;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't link Telegram");
      } finally {
        setPending(false);
      }
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", env.VITE_TELEGRAM_BOT_USERNAME);
    script.setAttribute("data-size", "medium");
    script.setAttribute("data-userpic", "false");
    script.setAttribute("data-radius", "10");
    script.setAttribute("data-onauth", "onTelegramLinkAuth(user)");
    script.setAttribute("data-request-access", "write");

    const wrapper = wrapperRef.current;
    wrapper?.appendChild(script);

    return () => {
      delete window.onTelegramLinkAuth;
      // Clean up the script node so a re-mount (e.g. after `isLinked`
      // flips back to false) starts fresh.
      if (wrapper && script.parentNode === wrapper) {
        wrapper.removeChild(script);
      }
    };
  }, [isLoading, isLinked, queryClient]);

  return (
    <Card padding="md" className="mt-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#dbeafe] text-[#2563eb]">
          <Send className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
            Telegram
          </p>
          <p className="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
            {isLinked ? "Telegram linked" : "Get notified about new matches"}
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-[#6c757d]">
            {isLinked
              ? "Connected. We'll write to you in Telegram when someone you liked likes you back. Works for every event you participate in."
              : "Add Telegram to your account — bot notifications about matches will land in your chat, and next time you can sign in with one tap instead of the email code."}
          </p>
        </div>
      </div>

      <div className="mt-4">
        {isLinked ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex h-10 items-center gap-2 rounded-[10px] bg-[#dcfce7] px-4 text-[13px] font-semibold text-[#15803d]">
              <Check className="h-4 w-4" /> Connected
            </div>
            {confirmUnlink ? (
              <>
                <button
                  type="button"
                  onClick={handleUnlink}
                  disabled={unlinking}
                  className="inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#ef4444] bg-white px-4 text-[13px] font-semibold text-[#b91c1c] hover:bg-[#fef2f2] disabled:opacity-60"
                >
                  {unlinking ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Disconnecting…
                    </>
                  ) : (
                    "Confirm disconnect"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmUnlink(false)}
                  disabled={unlinking}
                  className="inline-flex h-10 items-center rounded-[10px] px-3 text-[13px] font-semibold text-[#6c757d] hover:bg-[#f1f5f9] disabled:opacity-60"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmUnlink(true)}
                className="inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#e2e8f0] bg-white px-4 text-[13px] font-semibold text-[#475569] hover:bg-[#f8fafc]"
              >
                <Link2Off className="h-4 w-4" />
                Disconnect
              </button>
            )}
            {error ? (
              <p className="basis-full text-[12px] font-semibold text-[#991b1b]">
                {error}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            {/* The widget script renders its own native button inside this
                div — we don't style it. Letting Telegram own the look
                avoids "is this real Telegram or a phishing button?". */}
            <div ref={wrapperRef} className="flex" />
            {pending ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-[#6c757d]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Linking…
              </p>
            ) : null}
            {error ? (
              <p className="mt-3 text-[12px] font-semibold text-[#991b1b]">
                {error}
                {conflict
                  ? " Sign in with that Telegram account directly if you want to merge."
                  : ""}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}
